import { useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Smile } from 'lucide-react'
import { EmojiPicker } from './EmojiPicker'
import { emojiImageUrl } from '@/lib/markdown'
import { cx } from '@/lib/cx'
import type { Emoji, ReactionEmoji } from '@/types/api'
import styles from './EmojiButton.module.css'

// размеры окна эмодзи — как .compact в MediaPicker.module.css
const PICKER_W = 352
const PICKER_H = 420

export interface PickedEmoji {
  id: string | null
  name: string | null
}

interface Props {
  value: PickedEmoji | null
  onChange: (e: PickedEmoji | null) => void
  /** Эмодзи серверов для выбора; пусто — только обычные. */
  custom?: { guild: string; emojis: Emoji[] }[] | undefined
  label: string
  className?: string | undefined
}

/** Выбранный эмодзи: картинка эмодзи сервера или символ. */
export function EmojiGlyph({ emoji, size = 20 }: { emoji: PickedEmoji; size?: number }) {
  if (emoji.id) return <img src={emojiImageUrl(emoji.id)} alt={emoji.name ?? ''} width={size} height={size} className={styles.image} draggable={false} />
  return <span className={styles.glyph} style={{ fontSize: Math.round(size * 0.9) }}>{emoji.name}</span>
}

/**
 * Кнопка выбора эмодзи для полей форм (вариант опроса, звук панели): окно эмодзи в портале поверх
 * модалки — внутри неё его обрезала бы рамка; открывается под кнопкой, не влезает — над ней.
 */
export function EmojiButton({ value, onChange, custom, label, className }: Props) {
  const button = useRef<HTMLButtonElement>(null)
  const [at, setAt] = useState<{ top: number; left: number; height: number } | null>(null)

  const open = () => {
    const r = button.current?.getBoundingClientRect()
    if (!r) return
    const w = Math.min(PICKER_W, window.innerWidth - 32)
    const below = window.innerHeight - r.bottom - 16
    const above = r.top - 16
    const down = below >= PICKER_H || below >= above
    const height = Math.max(200, Math.min(PICKER_H, down ? below : above))
    setAt({ top: down ? r.bottom + 8 : r.top - 8 - height, left: Math.max(16, Math.min(r.left, window.innerWidth - w - 16)), height })
  }

  const pick = (e: ReactionEmoji) => {
    onChange(e.id ? { id: e.id, name: e.name } : { id: null, name: e.name })
    setAt(null)
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        className={cx(styles.button, at && styles.on, className)}
        aria-label={label}
        data-tooltip={label}
        aria-haspopup="dialog"
        aria-expanded={Boolean(at)}
        // окно закрывается по нажатию мимо себя; без этого нажатие на кнопку закрыло бы его и тут же открыло
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => (at ? setAt(null) : open())}
      >
        {value && (value.id || value.name) ? <EmojiGlyph emoji={value} size={22} /> : <Smile size={20} />}
      </button>
      {at
        ? createPortal(
            <div className={styles.picker} style={{ top: at.top, left: at.left, '--picker-h': `${at.height}px` } as CSSProperties}>
              <EmojiPicker custom={custom ?? []} onPick={pick} onClose={() => setAt(null)} />
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
